import { create } from 'zustand';

export type PresenceStatus = 'active' | 'away';

/** One signed-in device in the same account, as the API's /presence namespace reports it. */
export interface PresenceMember {
  /** Device session, or the user when the token carries none. */
  key: string;
  userId: string;
  name: string;
  email: string;
  status: PresenceStatus;
  /** ISO time the member became active / away. */
  since: string;
  /** Path only — the API drops query strings. */
  path: string;
  /** The breadcrumbs of the page they are on, in their own language. */
  crumbs: string[];
  tabs: number;
  /** Ardealy support working in the account (an Act-as session), not a teammate. */
  support?: boolean;
}

/**
 * Who else is here. Outside React, like the bell's store, because the top bar
 * remounts on every navigation while the socket (PresenceProvider) does not.
 */
interface PresenceState {
  members: PresenceMember[];
  selfKey: string | null;
  selfUserId: string | null;
  /**
   * What the current page calls itself, reported by the top bar. Tagged with
   * its path so a page without a top bar never inherits the previous page's.
   */
  page: { path: string; crumbs: string[] } | null;
  setMembers: (members: PresenceMember[]) => void;
  setSelf: (key: string, userId: string) => void;
  reportPage: (path: string, crumbs: string[]) => void;
  reset: () => void;
}

export const usePresenceStore = create<PresenceState>((set, get) => ({
  members: [],
  selfKey: null,
  selfUserId: null,
  page: null,
  setMembers: (members) => set({ members: Array.isArray(members) ? members : [] }),
  setSelf: (selfKey, selfUserId) => set({ selfKey, selfUserId }),
  reportPage: (path, crumbs) => {
    const current = get().page;
    if (current && current.path === path && current.crumbs.join('\u0000') === crumbs.join('\u0000')) return;
    set({ page: { path, crumbs } });
  },
  reset: () => set({ members: [], selfKey: null, selfUserId: null }),
}));

const EMPTY: PresenceMember[] = [];

/** Everyone but this device. Memoised on the list so the selector stays referentially stable. */
let lastMembers: PresenceMember[] | null = null;
let lastSelf: string | null = null;
let lastOthers: PresenceMember[] = EMPTY;

export function selectOthers(state: Pick<PresenceState, 'members' | 'selfKey'>): PresenceMember[] {
  if (state.members === lastMembers && state.selfKey === lastSelf) return lastOthers;
  lastMembers = state.members;
  lastSelf = state.selfKey;
  // Until the server has said who we are, show no one rather than ourselves.
  lastOthers = state.selfKey ? state.members.filter((m) => m.key !== state.selfKey) : EMPTY;
  return lastOthers;
}
